import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { ExecutionRuntime } from '../execution-runtime';
import { JobType } from '../../../api/jobs.dao';
import {
    ContextExecutionParams,
    EventParams,
    JobConfigRegistry,
    JobDtoType, LongRunningJob
} from '../index';
import { IJobsMonitor, JobsSubscription } from '../../jobs-monitor/jobs-monitor';
import { EventExecutionContext } from './event-execution-context';
import { Job } from './job';
import jobsRegistry from './jobs-registry';
import { registerJobCompletionHandlerByType, reset as resetHandlersRegistry, runRegisteredHandlers, runRegisteredHandlersByType } from './execution-handlers-registry';
import { EventExecutionState } from './event-execution-state';
import { JobExecutionRunner } from './job-execution-runner';
import { JobResultHandler } from './job-result-handler';


@IoC.Singleton
export class EventExecutionRuntime extends ExecutionRuntime<EventExecutionContext, EventParams> {
    private static initJobsMonitorSubscriptions(...args: JobsSubscription[]): void {
        args.forEach(IoC.Container.get(IJobsMonitor).subscribe);
    }

    private jobResultHandler: JobResultHandler = new JobResultHandler();

    @mobx.observable
    private processing: boolean = false;

    private get jobsRegistry(): JobConfigRegistry { return this.registry; }

    public get processingInProgress(): boolean {
        return this.processing;
    }

    @mobx.action.bound
    public startProcessing(): void { this.processing = true; }
    @mobx.action.bound
    public stopProcessing(): void { this.processing = false; }

    public triggerEnabled(): boolean {
        return this.isReady();
    }

    public getParams(): ContextExecutionParams<EventParams> {
        return this.executionContext.getContextParams();
    }

    @mobx.action.bound
    public addExecutionToState(job: JobDtoType): void {
        this.executionState.addToState(job);
    }
    @mobx.action.bound
    private syncState(jobs: JobDtoType[]): void {
        this.executionState.setState(jobs);
    }

    private readonly executionContext = new EventExecutionContext();

    @mobx.observable
    private readonly executionState: EventExecutionState = new EventExecutionState();


    private getCurrentExecutionState(): JobExecutionRunner[] {
        return this.executionContext.isValid()
            ? this.executionState.getState().filter(jobRunner => jobRunner.inExecutionContext(this.getParams()))
            : []
        ;
    }

    private hasIncompleteExecution(): boolean {
        return this.getCurrentExecutionState().some(jobRunner => jobRunner.inProgress());
    }

    private getExecutionToProcess(): JobExecutionRunner {
        return this.getCurrentExecutionState()
            .find(jobRunner => jobRunner.isFinished());
    }

    @mobx.action
    private async finishExecutionProcessing(executionRunner): Promise<void> {
        await runRegisteredHandlers(executionRunner);
        await executionRunner.dismiss();
        this.executionState.removeFromState(executionRunner.id);
        this.stopProcessing();
    }

    @mobx.action.bound
    private processNextExecution(): Promise<void> {
        const execution = this.getExecutionToProcess();
        if (this.processingInProgress || !execution) return;

        if (execution.type === JobType.EventBlueprintTrigger || execution.type === JobType.MeetingBlueprintTrigger) return;

        this.startProcessing();

        this.jobResultHandler.handleJobExecutionResult(execution, this.finishExecutionProcessing.bind(this, execution));
    }


    /**
     * Returns configured Job ready for execution by type.
     */
    public getJobByType = (type: JobType): LongRunningJob => {
        return Job.configure(this.jobsRegistry[type], this);
    };

    /**
     * Registers Job completion handler by type
     */
    public onComplete(type: JobType, handler: (() => void)): void {
        registerJobCompletionHandlerByType(type, handler);
    }

    /**
     * Triggers registered completion handlers for a specific job type
     */
    public triggerHandlersByType(type: JobType): Promise<void> {
        return runRegisteredHandlersByType(type);
    }

    /**
     * Returns event execution context instance.
     */
    public getExecutionContext = (): EventExecutionContext => this.executionContext;

    /**
     * Returns ready state to indicate if the next job can be triggered.
     */
    public isReady = (): boolean => !this.processingInProgress && !this.hasIncompleteExecution();

    public revalidateExecutionState = async (): Promise<void> => this.executionState.initState();

    constructor(private readonly registry: JobConfigRegistry = jobsRegistry) {
        super();

        resetHandlersRegistry();
        this.executionState.initState()
            .finally(EventExecutionRuntime.initJobsMonitorSubscriptions.bind(this, this.syncState, this.processNextExecution));
    }
}

